import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/client';

const initialForm = {
  name: '',
  email: '',
  password: '',
  height: '',
  weight: '',
  age: '',
  gender: 'female',
  activityLevel: 'moderate',
  healthGoal: 'maintain',
};

const STEPS = [
  { id: 1, label: 'Account', icon: '👤' },
  { id: 2, label: 'Body', icon: '📏' },
  { id: 3, label: 'Goals', icon: '🎯' },
];

export default function Signup({ onLogin }) {
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const nextStep = (e) => {
    e.preventDefault();
    setStep((s) => Math.min(s + 1, 3));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 3) { nextStep(e); return; }
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.signup({
        ...form,
        height: Number(form.height),
        weight: Number(form.weight),
        age: Number(form.age),
      });
      onLogin(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#080b11',
      overflow: 'hidden',
    }}>
      {/* Left — Brand Panel */}
      <div style={{
        flex: '0 0 380px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, #0d1f0a 0%, #080b11 60%)',
      }}>
        <div style={{
          position: 'absolute', top: '8%', right: '10%',
          width: 260, height: 260, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(163,230,53,0.13) 0%, transparent 70%)',
          animation: 'float 7s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '10%', left: '5%',
          width: 180, height: 180, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(129,140,248,0.1) 0%, transparent 70%)',
          animation: 'float 9s ease-in-out infinite reverse',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 48 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11,
              background: 'linear-gradient(135deg, #a3e635 0%, #4ade80 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
            }}>🥗</div>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9', fontFamily: 'Inter, sans-serif' }}>
              NutriTrack
            </span>
          </div>

          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 12, color: '#f1f5f9' }}>
            Create your account
          </h2>
          <p style={{ color: 'rgba(241,245,249,0.45)', fontSize: 13, lineHeight: 1.7, marginBottom: 44 }}>
            We'll personalise your calorie goal and BMI using your body stats.
          </p>

          {/* Step indicators */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {STEPS.map((s) => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '13px 16px', borderRadius: 12,
                background: step === s.id ? 'rgba(163,230,53,0.1)' : 'transparent',
                border: `1px solid ${step === s.id ? 'rgba(163,230,53,0.25)' : 'transparent'}`,
                transition: 'all 0.3s',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: step > s.id
                    ? '#a3e635'
                    : step === s.id
                    ? 'rgba(163,230,53,0.15)'
                    : 'rgba(255,255,255,0.05)',
                  border: `1.5px solid ${step === s.id ? '#a3e635' : step > s.id ? '#a3e635' : 'rgba(255,255,255,0.1)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: step > s.id ? 14 : 13, fontWeight: 700,
                  color: step > s.id ? '#0a0d12' : step === s.id ? '#a3e635' : 'rgba(255,255,255,0.3)',
                  transition: 'all 0.3s',
                }}>
                  {step > s.id ? '✓' : s.id}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: step >= s.id ? '#f1f5f9' : 'rgba(241,245,249,0.3)' }}>
                    {s.icon} {s.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Form */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 48px',
        overflowY: 'auto',
      }}>
        <form
          onSubmit={handleSubmit}
          style={{
            width: '100%',
            maxWidth: 460,
            animation: 'fadeInUp 0.45s cubic-bezier(0.4,0,0.2,1) both',
          }}
        >
          {/* Step 1 — Account */}
          {step === 1 && (
            <div key="step1" style={{ animation: 'fadeInUp 0.3s ease both' }}>
              <StepHeader
                title="Account details"
                subtitle="Set up your login credentials"
                step="1 / 3"
              />
              {error && <div className="error-banner">⚠️ {error}</div>}

              <div className="input-group">
                <label>Full name</label>
                <input type="text" name="name" required value={form.name} onChange={handleChange} placeholder="Jane Doe" />
              </div>
              <div className="input-group">
                <label>Email address</label>
                <input type="email" name="email" required value={form.email} onChange={handleChange} placeholder="you@example.com" />
              </div>
              <div className="input-group" style={{ marginBottom: 32 }}>
                <label>Password</label>
                <input type="password" name="password" required minLength={6} value={form.password} onChange={handleChange} placeholder="Min. 6 characters" />
              </div>
            </div>
          )}

          {/* Step 2 — Body */}
          {step === 2 && (
            <div key="step2" style={{ animation: 'fadeInUp 0.3s ease both' }}>
              <StepHeader
                title="Body stats"
                subtitle="Used to calculate your BMI and calorie needs"
                step="2 / 3"
              />
              {error && <div className="error-banner">⚠️ {error}</div>}

              <div className="row">
                <div className="input-group">
                  <label>Height (cm)</label>
                  <input type="number" name="height" required value={form.height} onChange={handleChange} placeholder="170" />
                </div>
                <div className="input-group">
                  <label>Weight (kg)</label>
                  <input type="number" name="weight" required value={form.weight} onChange={handleChange} placeholder="65" />
                </div>
              </div>

              <div className="row" style={{ marginBottom: 32 }}>
                <div className="input-group">
                  <label>Age</label>
                  <input type="number" name="age" required value={form.age} onChange={handleChange} placeholder="25" />
                </div>
                <div className="input-group">
                  <label>Gender</label>
                  <select name="gender" value={form.gender} onChange={handleChange}>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Goals */}
          {step === 3 && (
            <div key="step3" style={{ animation: 'fadeInUp 0.3s ease both' }}>
              <StepHeader
                title="Your goals"
                subtitle="Personalise your nutrition targets"
                step="3 / 3"
              />
              {error && <div className="error-banner">⚠️ {error}</div>}

              <div className="input-group">
                <label>Activity level</label>
                <select name="activityLevel" value={form.activityLevel} onChange={handleChange}>
                  <option value="sedentary">Sedentary — little to no exercise</option>
                  <option value="light">Light — 1–3 days/week</option>
                  <option value="moderate">Moderate — 3–5 days/week</option>
                  <option value="active">Active — 6–7 days/week</option>
                  <option value="very_active">Very active — physical job</option>
                </select>
              </div>

              <div className="input-group" style={{ marginBottom: 32 }}>
                <label>Health goal</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
                  {[
                    { value: 'weight_loss', label: 'Lose weight', icon: '📉' },
                    { value: 'maintain', label: 'Maintain', icon: '⚖️' },
                    { value: 'weight_gain', label: 'Gain weight', icon: '📈' },
                    { value: 'muscle_gain', label: 'Build muscle', icon: '💪' },
                  ].map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, healthGoal: g.value }))}
                      style={{
                        padding: '14px 12px',
                        borderRadius: 10,
                        border: `1.5px solid ${form.healthGoal === g.value ? '#a3e635' : 'rgba(255,255,255,0.08)'}`,
                        background: form.healthGoal === g.value ? 'rgba(163,230,53,0.1)' : 'rgba(255,255,255,0.03)',
                        color: form.healthGoal === g.value ? '#a3e635' : 'rgba(241,245,249,0.6)',
                        fontSize: 13, fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                        transition: 'all 0.2s',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                      }}
                    >
                      <span style={{ fontSize: 22 }}>{g.icon}</span>
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Nav Buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(s => s - 1)}
                className="btn btn-ghost"
                style={{ flex: '0 0 auto', paddingLeft: 20, paddingRight: 20 }}
              >
                ← Back
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1, height: 48, fontSize: 15 }}
              disabled={loading}
            >
              {loading ? (
                <><span className="spinner" /> Creating…</>
              ) : step < 3 ? (
                'Continue →'
              ) : (
                'Create account →'
              )}
            </button>
          </div>

          <p style={{ marginTop: 24, fontSize: 14, textAlign: 'center', color: 'rgba(241,245,249,0.4)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#a3e635', fontWeight: 600 }}>
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function StepHeader({ title, subtitle, step }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{
        display: 'inline-block',
        fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
        color: '#a3e635', background: 'rgba(163,230,53,0.1)',
        border: '1px solid rgba(163,230,53,0.2)',
        padding: '4px 10px', borderRadius: 99,
        marginBottom: 14, fontFamily: 'Inter, sans-serif',
        textTransform: 'uppercase',
      }}>
        Step {step}
      </div>
      <h2 style={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 8 }}>
        {title}
      </h2>
      <p style={{ color: 'rgba(241,245,249,0.45)', fontSize: 14 }}>{subtitle}</p>
    </div>
  );
}
